import { useState, useCallback, ChangeEvent } from 'react'
import { isEmpty } from 'ramda'
import { useTranslation } from 'react-i18next'

import IconBtnGo from 'app/common/icons/IconBtnGo'
import Button from 'app/common/ui/Button'
import EmptyWarn from 'app/common/layouts/EmptyWarn'
import Loading from 'app/common/components/Loader'
import { BannerContent } from 'app/common/components-next/banner'
import SellPointsData, { SellPointView } from 'app/common/components-next/sell-points-data'
import { PageTemplateContent, Header2 } from 'app/common/templates-next/page-template'
import PlainDateMonthSwitcher from 'app/common/ui-next/plain-date-month-picker'
import PlainDateSwitcher from 'app/common/ui-next/plain-date-switcher'
import SimpleDate from 'app/common/classes/simple-date'

import CustomerSettingsApi from '../CustomerSettingsApi'
import MenuDishesApi from '../MenuDishesApi'
import MenuComplexesApi from '../MenuComplexesApi'
import { PurchasesApi, statuses } from '../PurchasesApi'
import Dishes from './Dishes'
import Complexes from '../_common/Complexes'
import OrderData from './OrderData'
import OrderForm from './OrderForm'
import CategoriesNav from './CategoriesNav'
import selectIsOrderAvailable from './selectIsOrderAvailable'
import selectDishes, { selectDishesByCategory } from './selectDishes'
import selectComplexes from '../_common/selectComplexes'

import styles from './CafeteriaMenu.module.scss'

type Props = {
  date: SimpleDate
  categoryId?: string
  header?: string
  orderDisabled?: boolean
  school: {
    id: number
    title: string
    isCamp?: boolean
  }
  person: {
    id: number
    name: {
      first: string
      last: string
      middle: string
      gender: 'male' | 'female' | 'androgynous'
    }
    timezone: string
  }
  route?: unknown // only for router v3
  disableOrder?: boolean
  showAvailableOnlyComplexes?: boolean
  onChangeDate: (date: string, dateFormat: string) => void
  onChangeCategory: (categoryId: string) => void
  createUrl: (params: { date?: SimpleDate; categoryId?: string }) => string
  onOrderSuccess?: () => void
}

/**
 * Компонент меню столовой. Выводит блюда меню по категориям.
 * Есть возможность создания заказа.
 */
const CafeteriaMenu = ({
  date,
  categoryId,
  header = 'Меню столовой',
  orderDisabled,
  school: { id: schoolId, isCamp },
  person: { id: personId, name: personName },
  route,
  disableOrder,
  showAvailableOnlyComplexes,
  onChangeDate,
  onChangeCategory,
  createUrl,
  onOrderSuccess,
}: Props) => {
  const { t } = useTranslation('cafeteria')

  const dateString = date.getStringFormatted(SimpleDate.defaultDateFormat)

  // состояние формы заказа (открыта/закрыта)
  const [open, setOpen] = useState(false)
  const handleToggleOrderForm = useCallback(
    (e: ChangeEvent<any>) => {
      if (e && e.preventDefault) {
        e.preventDefault()
      }
      setOpen(!open)
    },
    [open],
  )

  return (
    <SellPointsData
      personId={personId}
      schoolId={isCamp ? schoolId : undefined}
      title={t('menu_sellpoint', 'Точка продаж')}
      /* onSellPoint={setCurrentSellPoint} */
    >
      {(currentSellPointId) => (
        <CustomerSettingsApi personId={personId}>
          {/* API настроек пользователя столовой */}
          {({ content: contentSettings, loading: loadingS }) => (
            // API блюд
            <MenuDishesApi
              personId={personId}
              date={dateString}
              schoolId={isCamp ? schoolId : undefined}
              sellPointId={currentSellPointId}
            >
              {({ content: contentDishes, loading: loadingD }) => (
                // API комплектов
                <MenuComplexesApi
                  personId={personId}
                  date={dateString}
                  schoolId={isCamp ? schoolId : undefined}
                  sellPointId={currentSellPointId}
                >
                  {({ content: contentComplexes, loading: loadingC }) => {
                    // данные блюд, массив всех
                    const dishes = selectDishes(contentDishes)
                    // данные блюд, сгруппированные по id категории
                    const dishesByCategory = selectDishesByCategory(contentDishes)

                    // данные комплектов
                    const complexes = selectComplexes(contentComplexes, {
                      getAvailableOnly: showAvailableOnlyComplexes,
                    })

                    const noDishes = isEmpty(dishes)
                    const noComplexes = isEmpty(complexes)
                    const nothing = noDishes && noComplexes
                    const loading = loadingS || loadingD || loadingC

                    // расчет возможности заказа (не загрузка, заказ не запрещен из prop
                    // есть что добавить в заказ
                    // заказ разрешен из настроек пользователя)
                    const orderAvailable =
                      !loading &&
                      !orderDisabled &&
                      !nothing &&
                      !disableOrder &&
                      selectIsOrderAvailable(contentSettings, { date: dateString })

                    // доступное количество средств для заказа и общий баланс
                    const { purchase_available_on: balanceAvailable, balance } = contentSettings

                    return (
                      // API покупок (в данном случае заказов) only pending status
                      <PurchasesApi
                        personId={personId}
                        from={dateString}
                        to={dateString}
                        status={statuses.PENDING}
                      >
                        {/* Хранение данных заказа */}
                        <OrderData
                          personId={personId}
                          date={dateString}
                          dishes={contentDishes.items}
                          complexes={contentComplexes.complexes}
                        >
                          {({ orderTotalConsideringFreeItems, countByCategoryId }) => {
                            const balanceLeftConsideringFreeItems =
                              (balanceAvailable || 0) - orderTotalConsideringFreeItems

                            return (
                              <>
                                <PageTemplateContent borderBottom={false} smoothCornersTop flex>
                                  <Header2 skipMargin>{header}</Header2>

                                  <SellPointView
                                    className={styles.sellPointView}
                                    title={`${t('menu_sellpoint', 'Точка продаж')}:`}
                                  />
                                  <PlainDateSwitcher
                                    date={dateString}
                                    dateFormat={SimpleDate.defaultDateFormat}
                                    onDateChange={onChangeDate}
                                  >
                                    <PlainDateMonthSwitcher
                                      date={dateString}
                                      dateFormat={SimpleDate.defaultDateFormat}
                                      onChangeDate={onChangeDate}
                                      hideDateMonthSwitcher
                                    />
                                  </PlainDateSwitcher>
                                </PageTemplateContent>

                                <div className={styles.doubleSplit}>
                                  <div className={styles.doubleLeft}>
                                    {/* Строка с меню категорий и кнопкой заказа */}
                                    <PageTemplateContent
                                      className={styles.categoriesRow}
                                      removeTopSmoothCorners
                                      flex
                                    >
                                      {/* Навигация */}
                                      {nothing ? (
                                        <div className={styles.categoriesMenu} />
                                      ) : (
                                        <CategoriesNav // TODO вынести в page?
                                          personId={personId}
                                          className={styles.categoriesMenu}
                                          dishesExist={!noDishes}
                                          complexesExist={!noComplexes}
                                          dishesByCategory={dishesByCategory}
                                          loading={loading}
                                          countByCategoryId={countByCategoryId}
                                          schoolId={isCamp ? schoolId : undefined}
                                          categoryId={categoryId}
                                          onChangeCategory={onChangeCategory}
                                          createUrl={createUrl}
                                        />
                                      )}

                                      {/* Кнопка заказа, если есть */}
                                      {orderAvailable && !open && (
                                        <Button onClick={handleToggleOrderForm}>
                                          {t('menu_make_order_bt', 'Создать заказ')}
                                          <IconBtnGo />
                                        </Button>
                                      )}
                                    </PageTemplateContent>

                                    {/* Контент блюд */}
                                    <PageTemplateContent className={styles.items} flex skipPadding>
                                      <div className={styles.content}>
                                        {nothing && !loading && (
                                          <EmptyWarn>
                                            {t('menu_empty', 'Нет меню на выбранный день')}
                                          </EmptyWarn>
                                        )}

                                        {nothing && loading && <Loading />}

                                        {!nothing && categoryId === 'kits' && (
                                          <Complexes
                                            complexes={complexes}
                                            orderFormOpened={open}
                                            balanceLeft={balanceLeftConsideringFreeItems}
                                            loading={loading}
                                            personId={personId}
                                            date={dateString}
                                          />
                                        )}

                                        {!nothing && categoryId && categoryId !== 'kits' && (
                                          <Dishes
                                            dishes={dishesByCategory[categoryId]}
                                            orderFormOpened={open}
                                            balanceLeft={balanceLeftConsideringFreeItems}
                                            loading={loading}
                                          />
                                        )}
                                      </div>
                                      {!orderAvailable && !loading && (
                                        <BannerContent
                                          // leaveContainerOnEmpty
                                          refreshOnMount
                                          className={styles.bannerContent}
                                          placeId="d099e4c6-f44b-42ef-b5f9-a1d52c27dee5"
                                        />
                                      )}
                                    </PageTemplateContent>
                                  </div>

                                  {/* Форма заказа */}
                                  {orderAvailable && (
                                    <OrderForm
                                      date={date}
                                      personId={personId}
                                      personName={personName}
                                      onClose={handleToggleOrderForm}
                                      open={open}
                                      balance={balance}
                                      balanceAvailable={balanceAvailable}
                                      route={route}
                                      onOrderSuccess={onOrderSuccess}
                                    />
                                  )}
                                </div>
                              </>
                            )
                          }}
                        </OrderData>
                      </PurchasesApi>
                    )
                  }}
                </MenuComplexesApi>
              )}
            </MenuDishesApi>
          )}
        </CustomerSettingsApi>
      )}
    </SellPointsData>
  )
}

export default CafeteriaMenu
